import c from 'classnames';
import { useLinks } from 'core/components/linksContext';
import { showSnackbar } from 'core/components/snackbar';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { ITheme, WithStyles } from 'core/style/interfaces';
import { isFilledArray, isImpossibleDate, isMobile } from 'core/utils';
import {
    getFormFirstDate,
    getFormItemsCount,
    IGetFormItemsCountProductForm,
    isProductFormSubscriptionAccess,
    productFormSubscriptionTypes,
} from 'core/utils/product-forms/productForms';
import React from 'react';
import PayButtonContainer from 'site/components/payments/PayButtonContainer';
import { IPayProductForm } from 'site/components/payments/PayButtonPresent';
import { Product, ProductForm, ProductFormPaymentStatusEnum } from 'site/graphql';
import ProductDeadlineCounter from './ProductDeadlineCounter';
import ProductTrainingOptions, { IProductTrainingOptionsProductForm } from './ProductTrainingOptions';

export type IProductPurchaseSectionProductForm = IPayProductForm &
    Pick<ProductForm, 'paymentStatus' | 'price' | 'subscriptionAccess' | 'showNumberLessons'> & {
        withMeInSetForms: (IGetFormItemsCountProductForm & Pick<ProductForm, 'id' | 'subscriptionAccess' | 'title'>)[];
        product: Pick<Product, 'id' | 'name'>;
    } & IGetFormItemsCountProductForm &
    IProductTrainingOptionsProductForm;

type IProductPurchaseSectionProps = {
    availableProductForms: IProductPurchaseSectionProductForm[];
    selectedProductFormIndex: number;
    onProductFormSelect: (index: number) => void;
    buttonMode?: boolean;
} & WithStyles<typeof styles>;

const ProductPurchaseSection: React.FC<IProductPurchaseSectionProps> = ({
    availableProductForms,
    buttonMode,
    classes,
    onProductFormSelect,
    selectedProductFormIndex,
}) => {
    const links = useLinks();
    const selectedProductForm = availableProductForms[selectedProductFormIndex];
    const CourseListLink = links.courseList.Link;

    const renderStartDate = () => {
        const selectedProductForm = availableProductForms[selectedProductFormIndex];
        const selectedStartDate = getFormFirstDate(selectedProductForm);

        // Если у формы нет даты начала или дата начала = Скоро
        if (!selectedStartDate || isImpossibleDate(selectedStartDate)) {
            return null;
        }

        return selectedStartDate > new Date() ? (
            <>
                <p className={classes.infoTitle}>До начала занятия осталось</p>
                <ProductDeadlineCounter startDate={selectedStartDate.getTime()} />
            </>
        ) : (
            <p className={classes.infoText}>Занятие уже началось, но вы можете присоединиться</p>
        );
    };

    // Нет доступных форм
    if (!isFilledArray(availableProductForms)) {
        return (
            <p>
                Пока что здесь ничего нет. <br />
                <CourseListLink to={links.courseList.url()}>Перейти к выбору других курсов</CourseListLink>
            </p>
        );
    }

    const showPresentColumn =
        selectedProductForm.withMeInSetForms.length > 0 &&
        selectedProductForm.paymentStatus !== ProductFormPaymentStatusEnum.Already;
    const showSubscriptionStar = isProductFormSubscriptionAccess(selectedProductForm);

    return (
        <>
            <ProductTrainingOptions
                availableProductForms={availableProductForms}
                onProductFormSelect={onProductFormSelect}
                selectedProductFormIndex={selectedProductFormIndex}
            />

            {buttonMode && (
                <div className={classes.paybtn}>
                    <PayButtonContainer productForm={selectedProductForm} />
                </div>
            )}

            <div className={classes.info}>
                <p className={classes.infoTitle}>Название</p>
                <p className={classes.infoText} style={{ textTransform: 'uppercase' }}>
                    {selectedProductForm.title}
                </p>
                <p className={classes.infoText}>{selectedProductForm.product.name}</p>

                <p className={classes.infoTitle}>Стоимость</p>
                <p
                    className={c(classes.infoText, { [classes.subscriptionStar]: showSubscriptionStar })}
                    title={showSubscriptionStar ? 'Доступ при покупке любого курса' : null}
                >
                    {selectedProductForm.price === 0 ? 'Бесплатно' : `${selectedProductForm.price} РУБ`}
                </p>

                {selectedProductForm.type !== 'OFFER' && selectedProductForm.showNumberLessons && (
                    <>
                        <p className={classes.infoTitle}>Количество занятий</p>
                        <p className={classes.infoText}>{getFormItemsCount(selectedProductForm)}</p>
                    </>
                )}

                {(selectedProductForm.type === 'WEBINAR' || selectedProductForm.type === 'SEMINAR') &&
                    renderStartDate()}
            </div>

            {!buttonMode && showPresentColumn && selectedProductForm.showNumberLessons && (
                <div className={classes.presentContainer}>
                    <div className={classes.presentTitle}>В комплекте вы получите:</div>
                    {selectedProductForm.withMeInSetForms.map(pf => {
                        const showStar = productFormSubscriptionTypes.includes(pf.type) && pf.subscriptionAccess;

                        return (
                            <div
                                key={pf.id}
                                className={showStar ? classes.subscriptionStar : undefined}
                                onClick={() =>
                                    showStar &&
                                    isMobile() &&
                                    showSnackbar({ snackbarMessage: 'Доступ при покупке любого курса' })
                                }
                                title={showStar ? 'Доступ при покупке любого курса' : null}
                            >
                                {pf.title} - {getFormItemsCount(pf)}
                            </div>
                        );
                    })}
                </div>
            )}
        </>
    );
};

const styles = (theme: ITheme) =>
    createStyles({
        info: {
            marginTop: 1.5 + 'rem',
        },
        infoTitle: {
            marginBottom: 0,
            textTransform: 'uppercase',
            marginTop: 1 + 'rem',
        },
        infoText: {
            fontWeight: 'bold',
            marginBottom: 0,
            fontSize: 1 + 'rem',
        },
        presentContainer: {
            marginTop: 1 + 'rem',
            backgroundColor: theme.colors.contrastBg,
            color: 'white',
            padding: 25,
            fontSize: 0.95 + 'rem',

            '& div:not(:first-child)': {
                marginTop: 0.5 + 'rem',
            },
        },
        presentTitle: {
            fontWeight: 'bold',
            fontSize: 1.2 + 'rem',
        },
        subscriptionStar: {
            '&:after': {
                content: '" *"',
            },
        },
        paybtn: {
            marginTop: 32,
        },
    });

export default injectSheet(styles, 'ProductPurchaseSection')(ProductPurchaseSection);
